import React, { useState, useEffect, useCallback, useRef } from 'react'; // useRef still used for toolsRef
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiPlus, FiTrash2, FiSave, FiZap, FiChevronDown, FiCopy, FiPrinter, FiSearch } from 'react-icons/fi';
import api from '../../services/api';
import TradePartnerAutocomplete from '../../components/common/TradePartnerAutocomplete';

const UNIT_OPTIONS = ['Shipment', 'B/L', 'Container', 'KG', 'LB', 'CBM', 'CFT', 'Pallet', 'Carton', 'Piece', 'Set', 'Lot'];
const PC_OPTIONS = [
  { value: 'P', label: 'Prepaid' },
  { value: 'C', label: 'Collect' },
];
const STATUS_OPTIONS = ['Draft', 'Open', 'Paid', 'Void'];
const INCOTERMS_OPTIONS = ['', 'EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP', 'FAS', 'FOB', 'CFR', 'CIF'];

/* ── Map shipment type to freight code categories ── */
const SHIPMENT_CATEGORY_MAP = {
  'ocean-import': ['Ocean Import', 'General'],
  'ocean-export': ['Ocean Export', 'General'],
  'air-import': ['Air Import', 'General'],
  'air-export': ['Air Export', 'General'],
  'truck': ['Trucking', 'General'],
};

/* ── Searchable Freight Code Dropdown ── */
const FreightCodePicker = ({ value, onSelect, onManualChange, shipmentType }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Keep displayed text in sync with external value changes
  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = (q) => {
    setQuery(q);
    onManualChange(q); // update the freight_code field as user types
    if (q.length < 1) { setResults([]); setOpen(false); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const categories = SHIPMENT_CATEGORY_MAP[shipmentType];
        const catParam = categories ? `&category=${categories.join(',')}` : '';
        const res = await api.get(`/freight-codes?q=${encodeURIComponent(q)}${catParam}`);
        setResults(res.data);
        setOpen(true);
      } catch { setResults([]); }
      setLoading(false);
    }, 200);
  };

  const pick = (code) => {
    // Strip the category prefix (AE, OI, TK, etc.) and show "01 - TRUCKING" format
    const cleanCode = code.code.replace(/^[A-Z]{2}/, '');
    const label = `${cleanCode} - ${code.description}`;
    setQuery(label);
    setOpen(false);
    onSelect({ ...code, code: label });
  };

  const pickOther = () => {
    setQuery('');
    setOpen(false);
    onSelect({ code: '', description: '', default_unit: null, default_rate: null, _isOther: true });
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        type="text"
        className="cell-input"
        value={query}
        onChange={(e) => search(e.target.value)}
        onFocus={() => { if (query.length >= 1 && results.length > 0) setOpen(true); }}
        placeholder="Search code..."
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,.12)', maxHeight: '220px', overflowY: 'auto',
        }}>
          {results.map(c => (
            <div
              key={c.id}
              onClick={() => pick(c)}
              style={{
                padding: '7px 10px', cursor: 'pointer', fontSize: '12px',
                borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f7ff'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
            >
              <span>{c.code.replace(/^[A-Z]{2}/, '')} - {c.description}</span>
            </div>
          ))}
          {/* Others option — always shown at the bottom */}
          <div
            onClick={pickOther}
            style={{
              padding: '7px 10px', cursor: 'pointer', fontSize: '12px',
              borderTop: results.length > 0 ? '2px solid #e2e8f0' : 'none',
              color: '#6366f1', fontStyle: 'italic',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f7ff'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
          >
            Other - type your own description
          </div>
          {loading && <div style={{ padding: '6px 10px', fontSize: '11px', color: '#94a3b8' }}>Loading...</div>}
        </div>
      )}
    </div>
  );
};

const ChargeDetail = () => {
  const navigate = useNavigate();
  const { chargeId } = useParams();
  const [searchParams] = useSearchParams();
  const shipmentIdParam = searchParams.get('shipmentId');
  const chargeTypeParam = searchParams.get('type') || 'AR';
  const shipmentTypeParam = searchParams.get('shipmentType') || 'truck';
  const isNew = !chargeId || chargeId === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [charge, setCharge] = useState(null);
  const [shipment, setShipment] = useState(null);
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({
    charge_type: chargeTypeParam,
    invoice_no: '',
    party: '',
    bill_to: '',
    attention_to: '',
    last_check_no: '',
    status: 'Draft',
    post_date: new Date().toISOString().split('T')[0],
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    ref_po_no: '',
    incoterms: '',
    memo: '',
    // DC-specific
    agent_name: '',
    agent_ref_no: '',
    profit_share_pct: 0,
    // AP-specific
    vendor: '',
    vendor_invoice_no: '',
    internal_ap_no: '',
    invoice_received: false,
  });

  const [showTools, setShowTools] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySearch, setCopySearch] = useState('');
  const [copyResults, setCopyResults] = useState([]);
  const [copySearching, setCopySearching] = useState(false);
  const toolsRef = useRef(null);

  // Close tools dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) {
        setShowTools(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const chargeType = formData.charge_type;

  const getTitle = () => {
    if (chargeType === 'AR') return 'Invoice (A/R)';
    if (chargeType === 'DC') return 'Debit/Credit Note';
    if (chargeType === 'AP') return 'Cost (A/P)';
    return 'Charge';
  };

  const getNoLabel = () => {
    if (chargeType === 'AR') return 'Invoice No.';
    if (chargeType === 'DC') return 'Debit/Credit No.';
    if (chargeType === 'AP') return 'Internal A/P No.';
    return 'No.';
  };

  const loadExisting = useCallback(async (id) => {
    try {
      const [chargeRes, itemsRes] = await Promise.all([
        api.get(`/charges/${id}`),
        api.get(`/charge-items/charge/${id}`),
      ]);

      const data = chargeRes.data;
      setCharge(data);
      setShipment(data.shipment);
      setItems(itemsRes.data);

      setFormData({
        charge_type: data.charge_type || 'AR',
        invoice_no: data.invoice_no || '',
        party: data.party || '',
        bill_to: data.bill_to || '',
        attention_to: data.attention_to || '',
        last_check_no: data.last_check_no || '',
        status: data.status || 'Draft',
        post_date: data.post_date ? data.post_date.split('T')[0] : '',
        invoice_date: data.invoice_date ? data.invoice_date.split('T')[0] : '',
        due_date: data.due_date ? data.due_date.split('T')[0] : '',
        ref_po_no: data.ref_po_no || '',
        incoterms: data.incoterms || '',
        memo: data.memo || '',
        agent_name: data.agent_name || '',
        agent_ref_no: data.agent_ref_no || '',
        profit_share_pct: data.profit_share_pct || 0,
        vendor: data.vendor || '',
        vendor_invoice_no: data.vendor_invoice_no || '',
        internal_ap_no: data.internal_ap_no || '',
        invoice_received: data.invoice_received || false,
      });
    } catch (error) {
      toast.error('Failed to load charge details');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Load shipment info for display when creating new charge
  const loadShipmentInfo = useCallback(async (sid, sType) => {
    try {
      const tableMap = { 'truck': 'truck', 'ocean-import': 'ocean-import', 'air-import': 'air-import', 'ocean-export': 'ocean-export' };
      const prefix = tableMap[sType] || 'truck';
      const res = await api.get(`/${prefix}/${sid}`);
      setShipment(res.data);
    } catch {
      // shipment info is optional for display
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isNew && shipmentIdParam) {
      // Don't auto-create; just load shipment info for display
      loadShipmentInfo(shipmentIdParam, shipmentTypeParam);
    } else if (!isNew && chargeId) {
      loadExisting(chargeId);
    } else {
      setLoading(false);
    }
  }, [isNew, chargeId, shipmentIdParam, shipmentTypeParam, loadExisting, loadShipmentInfo]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Auto-generate invoice number
  const autoGenerateNo = async () => {
    try {
      const res = await api.post('/charges/generate-number', { charge_type: chargeType });
      setFormData(prev => ({ ...prev, invoice_no: res.data.invoice_no }));
      toast.success(`Generated: ${res.data.invoice_no}`);
    } catch (error) {
      toast.error('Failed to generate number');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Auto-set party from the type-specific name field if party is empty
      const dataToSave = { ...formData };
      if (!dataToSave.party) {
        if (dataToSave.charge_type === 'AP' && dataToSave.vendor) {
          dataToSave.party = dataToSave.vendor;
        } else if (dataToSave.charge_type === 'AR' && dataToSave.bill_to) {
          dataToSave.party = dataToSave.bill_to;
        } else if (dataToSave.charge_type === 'DC' && dataToSave.agent_name) {
          dataToSave.party = dataToSave.agent_name;
        }
      }

      if (isNew && shipmentIdParam) {
        // First save — create the charge record
        const res = await api.post(`/charges/shipment/${shipmentIdParam}`, {
          ...dataToSave,
          shipment_type: shipmentTypeParam,
        });
        toast.success(`${getTitle()} created`);
        navigate(`/charge/${res.data.id}`, { replace: true });
      } else if (!isNew) {
        await api.put(`/charges/${chargeId}`, dataToSave);
        toast.success(`${getTitle()} updated`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // --- Line Items ---
  const addLineItem = async () => {
    if (isNew) {
      toast.info('Please save the charge first before adding line items.');
      return;
    }
    try {
      const res = await api.post(`/charge-items/charge/${chargeId}`, {
        freight_code: '',
        freight_description: '',
        bill_type: chargeType === 'DC' ? 'Debit' : '',
        unit: 'Shipment',
        volume: 1,
        rate: 0,
        amount: 0,
      });
      setItems(prev => [...prev, res.data]);
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const updateLineItem = async (id, field, value) => {
    // A D/C line can carry its money on either side: `amount` is revenue (what
    // the agent owes us) and `agent_amount` is cost (what we owe them). Rate x
    // volume has to land on the side the line is already on, or editing the
    // rate of a cost line silently moves the money to revenue.
    const current = items.find(i => i.id === id);
    const onCostSide = !!current
      && Number(current.agent_amount) > 0 && !Number(current.amount);
    const moneyField = onCostSide ? 'agent_amount' : 'amount';
    let recomputed = null;
    if (field === 'volume' || field === 'rate') {
      const vol = field === 'volume' ? Number(value) : Number(current && current.volume);
      const rate = field === 'rate' ? Number(value) : Number(current && current.rate);
      recomputed = (vol * rate).toFixed(2);
    }
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (recomputed !== null) updated[moneyField] = recomputed;
        return updated;
      }
      return item;
    }));

    try {
      // Send the recomputed money field too. This used to PUT only the edited
      // field, so changing a rate updated the row on screen and left the stored
      // amount alone: the charge total never moved and a refresh put the old
      // figure back. Fay hit exactly this correcting a WFG rate from 95 to 90.
      //
      // `amount` goes on EVERY rate/volume edit, even on a cost line where it
      // must stay 0. routes/chargeItems.js does its own `amount = volume *
      // rate` whenever rate or volume arrives without an explicit amount, so
      // sending {rate, agent_amount} alone would have the server refill the
      // revenue side of a cost line and undo the direction on save.
      const payload = { [field]: value };
      if (recomputed !== null) {
        payload[moneyField] = recomputed;
        if (moneyField !== 'amount') payload.amount = 0;
      }
      await api.put(`/charge-items/${id}`, payload);
      const chargeRes = await api.get(`/charges/${chargeId}`);
      setCharge(chargeRes.data);
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const deleteLineItem = async (id) => {
    try {
      await api.delete(`/charge-items/${id}`);
      setItems(prev => prev.filter(item => item.id !== id));
      const chargeRes = await api.get(`/charges/${chargeId}`);
      setCharge(chargeRes.data);
      toast.success('Item deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const itemsTotal = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  // If no line items yet, fall back to the header-level revenue/cost
  const totalAmount = itemsTotal > 0 ? itemsTotal : (
    charge ? (Number(charge.revenue) || Number(charge.cost) || 0) : 0
  );
  const totalAgentAmount = items.reduce((sum, item) => sum + (Number(item.agent_amount) || 0), 0);

  // DC-specific: split by Debit/Credit type
  const debitItems = items.filter(item => (item.bill_type || 'Debit') === 'Debit');
  const creditItems = items.filter(item => item.bill_type === 'Credit');
  // A D/C line's money sits in `amount` (revenue) OR `agent_amount` (cost) —
  // never both — so the footer has to look at whichever side carries it.
  // Summing `amount` alone reported every cost line as 0 the moment cost lines
  // became enterable. With both sides counted, Debit - Credit reproduces the
  // agent's own DUE TO line: SHINE's SHSC26080033 is 175 debit - 275 credit =
  // -100, and her note prints DUE TO JINTONG -100.00.
  const lineMoney = (item) => (Number(item.amount) || 0) + (Number(item.agent_amount) || 0);
  const totalDebit = debitItems.reduce((sum, item) => sum + lineMoney(item), 0);
  const totalCredit = creditItems.reduce((sum, item) => sum + lineMoney(item), 0);

  // --- Tools Actions ---
  const handleCopyTo = async () => {
    if (isNew) return;
    if (!window.confirm(`Copy this ${getTitle()} to a new one under the same shipment?`)) return;
    try {
      const res = await api.post(`/charges/${chargeId}/copy-to`);
      toast.success('Copied! Opening new charge...');
      navigate(`/charge/${res.data.id}`);
    } catch (error) {
      toast.error('Failed to copy');
    }
    setShowTools(false);
  };

  const handleCopySearch = async (q) => {
    setCopySearch(q);
    if (q.length < 2) { setCopyResults([]); return; }
    setCopySearching(true);
    try {
      const res = await api.get(`/charges/search-shipments?q=${encodeURIComponent(q)}`);
      setCopyResults(res.data);
    } catch { setCopyResults([]); }
    setCopySearching(false);
  };

  const handleCopyToShipment = async (targetShipment) => {
    try {
      const res = await api.post(`/charges/${chargeId}/copy`, {
        target_shipment_id: targetShipment.id,
        target_shipment_type: targetShipment.shipment_type,
      });
      toast.success(`Copied to ${targetShipment.file_no}!`);
      setShowCopyModal(false);
      setCopySearch('');
      setCopyResults([]);
      navigate(`/charge/${res.data.id}`);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleDelete = async () => {
    if (isNew) return;
    if (!window.confirm(`Delete this ${getTitle()}? This cannot be undone.`)) return;
    try {
      await api.delete(`/charges/${chargeId}`);
      toast.success(`${getTitle()} deleted`);
      goBack();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handlePrint = () => {
    if (chargeType === 'AR') {
      window.open(`/documents/charge/${chargeId}/invoice`, '_blank');
    } else if (chargeType === 'DC') {
      window.open(`/documents/charge/${chargeId}/credit-note`, '_blank');
    }
    setShowTools(false);
  };

  const rawType = charge?.shipment_type || shipmentTypeParam || 'truck';
  // Normalize underscore variants to hyphen for URL routing (e.g. air_import → air-import)
  const shipmentTypePath = rawType.replace(/_/g, '-');

  const goBack = () => {
    const sid = shipment?.id || shipmentIdParam;
    if (sid) {
      navigate(`/${shipmentTypePath}/${sid}?tab=Accounting`);
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return <div className="loading"><div className="loading-spinner"></div></div>;
  }

  return (
    <div className="shipment-page charge-detail-page">
      {/* Breadcrumb */}
      <div className="shipment-breadcrumb">
        <button className="breadcrumb-back-btn" onClick={goBack} title="Back">
          <FiArrowLeft />
        </button>
        <span>Home</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span style={{ textTransform: 'capitalize' }}>{shipmentTypePath.replace('-', ' ')}</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span className="breadcrumb-link" onClick={goBack} style={{ cursor: 'pointer', color: '#3b82f6' }}>
          {shipment?.file_no || 'Shipment'}
        </span>
        <span className="breadcrumb-sep">&gt;</span>
        <span className="breadcrumb-current">{formData.invoice_no || getTitle()}</span>
      </div>

      {/* Page Header */}
      <div className="charge-detail-header">
        <h2 className="charge-detail-title">{getTitle()}</h2>
        <div className="charge-detail-header-actions">
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className={`charge-status-badge status-${formData.status.toLowerCase().replace(' ', '-')}`}
          >
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="shipment-save-btn" onClick={handleSave} disabled={saving}>
            <FiSave size={14} />
            {saving ? 'Saving...' : 'Save'}
          </button>

          {/* Tools Dropdown */}
          {!isNew && (
            <div className="tools-dropdown-wrapper" ref={toolsRef}>
              <button className="tools-dropdown-btn" onClick={() => setShowTools(!showTools)}>
                Tools <FiChevronDown size={14} />
              </button>
              {showTools && (
                <div className="tools-dropdown-menu">
                  <div className="tools-menu-section">Actions</div>
                  <button onClick={handleCopyTo}>
                    <FiCopy size={13} /> Copy To (Same Shipment)
                  </button>
                  <button onClick={() => { setShowCopyModal(true); setShowTools(false); }}>
                    <FiSearch size={13} /> Copy (Different Shipment)
                  </button>
                  <button onClick={handleDelete} className="tools-menu-danger">
                    <FiTrash2 size={13} /> Delete
                  </button>
                  {chargeType !== 'AP' && (
                    <>
                      <div className="tools-menu-section">Documents</div>
                      <button onClick={handlePrint}>
                        <FiPrinter size={13} /> Print {chargeType === 'AR' ? 'Invoice' : 'Credit Note'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Copy to Different Shipment Modal */}
      {showCopyModal && (
        <div className="modal-overlay" onClick={() => setShowCopyModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <h3 style={{ marginBottom: '12px' }}>Copy to Different Shipment</h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
              Search by File No., MB/L No., or HB/L No.
            </p>
            <input
              type="text"
              value={copySearch}
              onChange={e => handleCopySearch(e.target.value)}
              placeholder="Type to search shipments..."
              autoFocus
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', marginBottom: '8px' }}
            />
            {copySearching && <div style={{ fontSize: '12px', color: '#94a3b8' }}>Searching...</div>}
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {copyResults.map(s => (
                <div
                  key={`${s.shipment_type}-${s.id}`}
                  onClick={() => handleCopyToShipment(s)}
                  style={{
                    padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>{s.file_no}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{s.mbl_no || 'No MBL'}</div>
                  </div>
                  <span style={{ fontSize: '10px', padding: '2px 8px', backgroundColor: '#e2e8f0', borderRadius: '4px', textTransform: 'capitalize' }}>
                    {s.shipment_type.replace('-', ' ')}
                  </span>
                </div>
              ))}
              {copySearch.length >= 2 && copyResults.length === 0 && !copySearching && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No shipments found</div>
              )}
            </div>
            <div style={{ textAlign: 'right', marginTop: '12px' }}>
              <button className="btn-cancel" onClick={() => { setShowCopyModal(false); setCopySearch(''); setCopyResults([]); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Basic Information - from shipment */}
      <div className="charge-section">
        <div className="form-section-header">Basic Information</div>
        <div className="shipment-form-grid">
          <div className="shipment-form-group">
            <label>File No.</label>
            <input type="text" value={shipment?.file_no || ''} disabled />
          </div>
          <div className="shipment-form-group">
            <label>{(formData.shipment_type === 'air_import' || formData.shipment_type === 'air-import') ? 'MAWB No.' : 'MB/L No.'}</label>
            <input type="text" value={shipment?.mbl_no || shipment?.mawb_no || ''} disabled />
          </div>
          <div className="shipment-form-group">
            <label>{(formData.shipment_type === 'air_import' || formData.shipment_type === 'air-import') ? 'HAWB No.' : 'HB/L No.'}</label>
            <input type="text" value={shipment?.hbl_no || ''} disabled />
          </div>
          <div className="shipment-form-group">
            <label>Consignee</label>
            <input type="text" value={shipment?.consignee || ''} disabled />
          </div>
        </div>
        <div className="shipment-form-grid">
          <div className="shipment-form-group">
            <label>Customer</label>
            <input type="text" value={shipment?.customer || ''} disabled />
          </div>
          <div className="shipment-form-group">
            <label>Shipper</label>
            <input type="text" value={shipment?.shipper || ''} disabled />
          </div>
          <div className="shipment-form-group">
            <label>Port of Loading</label>
            <input type="text" value={shipment?.port_of_loading || ''} disabled />
          </div>
          <div className="shipment-form-group">
            <label>Port of Discharge</label>
            <input type="text" value={shipment?.port_of_discharge || ''} disabled />
          </div>
        </div>
      </div>

      <div className="shipment-section-divider"></div>

      {/* Billing Information */}
      <div className="charge-section">
        <div className="form-section-header">Billing Information</div>

        <div className="shipment-form-grid">
          {/* Invoice/DC No - editable with auto-generate button for AR and DC; plain input for AP */}
          <div className="shipment-form-group">
            <label>{getNoLabel()}</label>
            {chargeType === 'AP' ? (
              <input
                type="text"
                name="invoice_no"
                value={formData.invoice_no}
                onChange={handleChange}
                placeholder="Vendor bill number"
              />
            ) : (
              <div className="input-with-btn">
                <input
                  type="text"
                  name="invoice_no"
                  value={formData.invoice_no}
                  onChange={handleChange}
                  placeholder="Type or auto-generate"
                />
                <button
                  type="button"
                  className="btn-auto-generate"
                  onClick={autoGenerateNo}
                  title="Auto-generate number"
                >
                  <FiZap size={13} />
                </button>
              </div>
            )}
          </div>

          {chargeType === 'AR' && (
            <>
              <div className="shipment-form-group">
                <label>Bill To</label>
                <TradePartnerAutocomplete
                  value={formData.bill_to}
                  onChange={(name, tp) => setFormData(prev => ({ ...prev, bill_to: name, party: tp ? name : prev.party, trade_partner_id: tp ? tp.id : prev.trade_partner_id }))}
                  placeholder="Search customer..."
                />
              </div>
              <div className="shipment-form-group">
                <label>Attention To</label>
                <input type="text" name="attention_to" value={formData.attention_to} onChange={handleChange} placeholder="Attention to" />
              </div>
              <div className="shipment-form-group">
                <label>Last Check No.</label>
                <input type="text" name="last_check_no" value={formData.last_check_no} onChange={handleChange} />
              </div>
            </>
          )}

          {chargeType === 'DC' && (
            <>
              <div className="shipment-form-group">
                <label>Agent Name</label>
                <TradePartnerAutocomplete
                  value={formData.agent_name}
                  onChange={(name, tp) => setFormData(prev => ({ ...prev, agent_name: name, party: tp ? name : prev.party, trade_partner_id: tp ? tp.id : prev.trade_partner_id }))}
                  tpTypes="Vendor,Customer,Others"
                  placeholder="Search agent..."
                />
              </div>
              <div className="shipment-form-group">
                <label>Agent Ref No.</label>
                <input type="text" name="agent_ref_no" value={formData.agent_ref_no} onChange={handleChange} />
              </div>
              <div className="shipment-form-group">
                <label>Profit Share %</label>
                <input type="number" step="0.01" name="profit_share_pct" value={formData.profit_share_pct} onChange={handleChange} />
              </div>
            </>
          )}

          {chargeType === 'AP' && (
            <>
              <div className="shipment-form-group">
                <label>Vendor</label>
                <TradePartnerAutocomplete
                  value={formData.vendor}
                  onChange={(name, tp) => setFormData(prev => ({ ...prev, vendor: name, party: tp ? name : prev.party, trade_partner_id: tp ? tp.id : prev.trade_partner_id }))}
                  tpTypes="Vendor,Trucker"
                  placeholder="Search vendor..."
                />
              </div>
              <div className="shipment-form-group">
                <label>Vendor Invoice No.</label>
                <input type="text" name="vendor_invoice_no" value={formData.vendor_invoice_no} onChange={handleChange} />
              </div>
              <div className="shipment-form-group">
                <label>Invoice Received</label>
                <div className="delivered-row">
                  <input type="checkbox" name="invoice_received" checked={formData.invoice_received} onChange={handleChange} />
                  <span style={{ fontSize: '13px', color: '#64748b' }}>{formData.invoice_received ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Date fields row */}
        <div className="shipment-form-grid">
          <div className="shipment-form-group">
            <label>Post Date</label>
            <input type="date" name="post_date" value={formData.post_date} onChange={handleChange} />
          </div>
          <div className="shipment-form-group">
            <label>Invoice Date</label>
            <input type="date" name="invoice_date" value={formData.invoice_date} onChange={handleChange} />
          </div>
          <div className="shipment-form-group">
            <label>Due Date</label>
            <input type="date" name="due_date" value={formData.due_date} onChange={handleChange} />
          </div>
          {chargeType === 'AR' && (
            <div className="shipment-form-group">
              <label>REF / P.O. No.</label>
              <input type="text" name="ref_po_no" value={formData.ref_po_no} onChange={handleChange} />
            </div>
          )}
        </div>

        {/* Amount Due (read-only, calculated from freight items) for AR */}
        {chargeType === 'AR' && (
          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Amount Due</label>
              <input type="text" value={formatCurrency(totalAmount)} disabled className="field-amount" />
            </div>
            <div className="shipment-form-group">
              <label>Incoterms</label>
              <select name="incoterms" value={formData.incoterms} onChange={handleChange}>
                {INCOTERMS_OPTIONS.map(t => <option key={t} value={t}>{t || '-- Select --'}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="shipment-section-divider"></div>

      {/* Freight Line Items Table */}
      <div className="charge-section">
        <div className="form-section-header">
          Freight
          <button className="btn-add-line-item" onClick={addLineItem}>
            <FiPlus size={12} /> Add Line
          </button>
        </div>

        <div className="charge-items-table-wrapper">
          <table className="charge-items-table">
            <thead>
              <tr>
                <th className="col-seq">#</th>
                <th>Freight Code</th>
                <th>Freight Description</th>
                {/* AR: P/C column | DC: Type (Debit/Credit) column | AP: no P/C */}
                {chargeType === 'AR' && <th>P/C</th>}
                {chargeType === 'DC' && <th>Type</th>}
                <th>Unit</th>
                <th className="col-number">Vol.</th>
                <th className="col-number">Rate</th>
                {/* A D/C note carries both directions, and which column the
                    money sits in IS the direction: the server derives
                    revenue = SUM(amount) and cost = SUM(agent_amount) for DC.
                    Only `amount` had an input, so a cost line could not be
                    entered at all and every D/C note came out as pure revenue
                    — SHINE's OI-26080224 showed a 100% margin on a shipment
                    where the agent owed us money (Fay, 2026-09-15). */}
                <th className="col-number" title={chargeType === 'DC'
                  ? 'Revenue — what the agent owes us' : 'Amount'}>
                  {chargeType === 'DC' ? 'Amount (Rev.)' : 'Amount'}
                </th>
                {chargeType === 'DC' && (
                  <th className="col-number" title="Cost — what we owe the agent">
                    Agent Amt (Cost)
                  </th>
                )}
                <th>Status</th>
                <th className="col-action"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={chargeType === 'AP' ? 9 : (chargeType === 'DC' ? 11 : 10)} className="empty-section-cell">
                    No freight items. Click "Add Line" to add.
                  </td>
                </tr>
              ) : (
                items.map((item, idx) => (
                  <tr key={item.id}>
                    <td className="col-seq">{idx + 1}</td>
                    <td>
                      <FreightCodePicker
                        value={item.freight_code || ''}
                        shipmentType={shipmentTypePath}
                        onManualChange={(val) => updateLineItem(item.id, 'freight_code', val)}
                        onSelect={(fc) => {
                          if (fc._isOther) {
                            // "Other" selected — set code to OTHER, let user type description freely
                            updateLineItem(item.id, 'freight_code', 'OTHER');
                            updateLineItem(item.id, 'freight_description', '');
                          } else {
                            // Auto-fill code, description, and unit from the selected freight code
                            updateLineItem(item.id, 'freight_code', fc.code);
                            updateLineItem(item.id, 'freight_description', fc.description);
                            if (fc.default_unit) updateLineItem(item.id, 'unit', fc.default_unit);
                            if (fc.default_rate) updateLineItem(item.id, 'rate', fc.default_rate);
                          }
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="cell-input"
                        value={item.freight_description || ''}
                        onChange={(e) => updateLineItem(item.id, 'freight_description', e.target.value)}
                        placeholder="Description"
                      />
                    </td>
                    {/* AR: Prepaid/Collect */}
                    {chargeType === 'AR' && (
                      <td>
                        <select
                          className="cell-select"
                          value={item.pc || 'P'}
                          onChange={(e) => updateLineItem(item.id, 'pc', e.target.value)}
                        >
                          {PC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                    )}
                    {/* DC: Debit/Credit type selector */}
                    {chargeType === 'DC' && (
                      <td>
                        <select
                          className="cell-select"
                          value={item.bill_type || 'Debit'}
                          onChange={(e) => updateLineItem(item.id, 'bill_type', e.target.value)}
                        >
                          <option value="Debit">Debit</option>
                          <option value="Credit">Credit</option>
                        </select>
                      </td>
                    )}
                    <td>
                      <select
                        className="cell-select"
                        value={item.unit || 'Shipment'}
                        onChange={(e) => updateLineItem(item.id, 'unit', e.target.value)}
                      >
                        {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </td>
                    <td className="col-number">
                      <input
                        type="number"
                        step="0.01"
                        className="cell-input cell-number"
                        value={item.volume || ''}
                        onChange={(e) => updateLineItem(item.id, 'volume', e.target.value)}
                        placeholder="1"
                      />
                    </td>
                    <td className="col-number">
                      <input
                        type="number"
                        step="0.01"
                        className="cell-input cell-number"
                        value={item.rate || ''}
                        onChange={(e) => updateLineItem(item.id, 'rate', e.target.value)}
                        placeholder="0.00"
                      />
                    </td>
                    <td className="col-number">
                      <input
                        type="number"
                        step="0.01"
                        className="cell-input cell-number"
                        value={item.amount || ''}
                        onChange={(e) => updateLineItem(item.id, 'amount', e.target.value)}
                        placeholder="0.00"
                      />
                    </td>
                    {chargeType === 'DC' && (
                      <td className="col-number">
                        <input
                          type="number"
                          step="0.01"
                          className="cell-input cell-number"
                          value={item.agent_amount || ''}
                          onChange={(e) => updateLineItem(item.id, 'agent_amount', e.target.value)}
                          placeholder="0.00"
                          title="Cost — what we owe the agent"
                        />
                      </td>
                    )}
                    <td>
                      <select
                        className="cell-select"
                        value={item.status || 'Open'}
                        onChange={(e) => updateLineItem(item.id, 'status', e.target.value)}
                      >
                        <option value="Open">Open</option>
                        <option value="Closed">Closed</option>
                        <option value="Void">Void</option>
                      </select>
                    </td>
                    <td className="col-action">
                      <button className="btn-action-delete" onClick={() => deleteLineItem(item.id)} title="Delete">
                        <FiTrash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              {/* AR and AP: simple Total Amount */}
              {chargeType !== 'DC' && (
                <tr className="totals-row">
                  <td colSpan={chargeType === 'AP' ? 7 : 8} className="totals-label">Total Amount</td>
                  <td className={`col-number total-amount ${totalAmount >= 0 ? 'amount-positive' : 'amount-negative'}`}>
                    {formatCurrency(totalAmount)}
                  </td>
                  <td></td>
                  <td></td>
                </tr>
              )}
              {/* DC: Debit Amount, Credit Amount, Total */}
              {chargeType === 'DC' && (
                <>
                  <tr className="totals-row sub-total-row">
                    <td colSpan={7} className="totals-label">Debit Amount</td>
                    <td className="col-number total-amount amount-positive">{formatCurrency(totalDebit)}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                  <tr className="totals-row sub-total-row">
                    <td colSpan={7} className="totals-label">Credit Amount</td>
                    <td className="col-number total-amount amount-negative">{formatCurrency(totalCredit)}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                  <tr className="totals-row">
                    <td colSpan={7} className="totals-label">Total Amount</td>
                    <td className={`col-number total-amount ${(totalDebit - totalCredit) >= 0 ? 'amount-positive' : 'amount-negative'}`}>
                      {formatCurrency(totalDebit - totalCredit)}
                    </td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                </>
              )}
            </tfoot>
          </table>
        </div>
      </div>

      <div className="shipment-section-divider"></div>

      {/* Memo */}
      <div className="charge-section">
        <div className="form-section-header">Memo</div>
        <div className="shipment-form-group">
          <textarea
            name="memo"
            value={formData.memo}
            onChange={handleChange}
            className="shipment-memo"
            placeholder="Add notes here..."
          />
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="shipment-form-actions">
        <button type="button" className="btn-cancel" onClick={goBack}>
          Back to Shipment
        </button>
        <button className="shipment-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

function formatCurrency(val) {
  const num = Number(val) || 0;
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default ChargeDetail;
